import { useEffect, useRef } from 'react';
import { useMessages } from './i18n/context';

/**
 * An in-DOM confirmation (ADR-029 §1) replacing `window.confirm`: native confirm sheets look
 * alien in WKWebView and are blocked in some Android WebView configurations, and a rendered
 * dialog is testable in every layer. It replaces the panel it confirms for (nothing to trap
 * focus from), takes focus on its safe button, and Escape cancels. Presentational — the caller
 * owns the action.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useMessages();
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => cancel.current?.focus(), []);

  return (
    <section
      className="dialog-panel confirm-dialog"
      role="dialog"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-message"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div className="dialog-heading">
        <h2 id="confirm-title">{title}</h2>
      </div>
      <p id="confirm-message">{message}</p>
      <div className="dialog-actions">
        <button className="button-danger" onClick={onConfirm} disabled={busy}>
          {confirmLabel}
        </button>
        <button className="button-secondary" ref={cancel} onClick={onCancel} disabled={busy}>
          {t.confirm.cancel}
        </button>
      </div>
    </section>
  );
}
